import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";

const journey = [
  {
    year: "2016",
    title: "It started with one idea.",
    desc: "Two engineering graduates set out to make healthier food more accessible without compromising on flavour.",
  },
  {
    year: "2016",
    title: "Our first GreeNox opens.",
    desc: "The first outlet brings the concept to life: wholesome food in a casual, approachable setting.",
  },
  {
    year: "Then",
    title: "One outlet became a growing community.",
    desc: "GreeNox expanded across Delhi NCR, bringing its menu to campuses, workplaces and neighbourhoods.",
  },
  {
    year: "Today",
    title: "21+ outlets and growing.",
    desc: "GreeNox is now a fast-casual food brand serving salads, bowls, wraps, subs, pastas and more across Delhi NCR.",
  },
  {
    year: "Next",
    title: "More cities. More people. Better food.",
    desc: "We're building GreeNox into a brand that makes better everyday eating easier.",
  },
];

// Pass headingTag="h2" when the page already has its own h1 (e.g. Home),
// and showReadMore to link through to the full About page.
export const StoryHero = ({ headingTag: Heading = "h1", showReadMore = false }) => {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-10 pt-2">
      <div className="story-hero relative overflow-hidden">
        <div className="relative z-10 px-6 py-10 md:px-10 md:py-12 lg:w-[52%] lg:pr-4">
          <p className="story-hero-head mb-4 text-base font-bold uppercase tracking-wide text-gray-900 md:text-lg">
            The <span className="normal-case">GreeNox</span> Story
          </p>
          <Heading className="story-hero-head mb-5 text-[2rem] font-bold uppercase leading-[1.15] text-[#1f5c33] md:text-[2.75rem]">
            You are what you eat.
            <br />
            So eat something good.
          </Heading>
          <p className="mb-4 text-lg font-semibold leading-snug text-gray-900">
            GreeNox started in 2016 with a simple idea: healthy food
            shouldn't feel like a compromise.
          </p>
          <div className="space-y-3 text-[15px] leading-relaxed text-gray-700">
            <p>
              What began as one restaurant built around wholesome, satisfying
              food has grown into a fast-casual food brand serving everyday
              meals across Delhi NCR.
            </p>
            <p>
              From fresh salads and protein-packed bowls to wraps, subs,
              pastas and more, we make food that brings together fresh
              ingredients, bold flavours and balanced choices.
              <br />
              Because eating well doesn't have to mean eating boring.
            </p>
          </div>
          {showReadMore && (
            <Link
              to="/about"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1f5c33] hover:underline"
            >
              Read our story
              <FiArrowRight
                aria-hidden="true"
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          )}
        </div>

        <div className="story-hero-media relative h-72 sm:h-96 lg:absolute lg:inset-y-0 lg:right-0 lg:h-auto lg:w-[52%]">
          <img
            src="/img/outlet.jpeg"
            alt="GreeNox outlet storefront"
            className="story-hero-img h-full w-full object-cover"
          />
        </div>
        <p
          className="story-hero-note absolute bottom-6 left-6 z-20 text-[1.7rem] leading-[1.05] text-gray-800 lg:bottom-10 lg:left-[51%]"
          aria-label="Food brings us together"
        >
          Food
          <br />
          <span className="pl-3">Brings Us</span>
          <br />
          <span className="pl-8">
            Together
            <svg
              aria-hidden="true"
              viewBox="0 0 24 22"
              className="ml-1 inline-block h-6 w-6 -translate-y-1 rotate-12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20.5C6 16 2 12.4 2 7.6 2 4.5 4.4 2 7.3 2c2 0 3.7 1.1 4.7 2.8C13 3.1 14.7 2 16.7 2 19.6 2 22 4.5 22 7.6c0 4.8-4 8.4-10 12.9z" />
            </svg>
          </span>
        </p>
      </div>
    </section>
  );
};

export const OurJourney = () => {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:py-20">
      <div>
        <div className="mb-12 text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-[#1f5c33]">
            Our Journey
          </p>
          <h2 className="story-hero-head text-3xl font-bold text-gray-900 md:text-4xl">
            From one outlet to a growing community.
          </h2>
        </div>

        <ol className="relative grid gap-8 md:grid-cols-5 md:gap-0">
          {/* Connecting line: horizontal on desktop, vertical on mobile */}
          <span
            aria-hidden="true"
            className="absolute left-[11px] top-3 h-[calc(100%-1.5rem)] w-px bg-[#1f5c33]/30 md:left-3 md:right-3 md:h-px md:w-auto"
          />
          {journey.map((item, idx) => (
            <li
              key={idx}
              className="relative pl-10 md:pl-0 md:pr-6 md:[&:not(:first-child)]:border-l md:[&:not(:first-child)]:border-gray-200 md:[&:not(:first-child)]:pl-6"
            >
              <span className="absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-[#1f5c33] text-xs font-bold text-white ring-4 ring-white md:static md:mb-5">
                {idx + 1}
              </span>
              <div className="text-xl font-bold text-gray-900">
                {item.year}
              </div>
              <div className="mb-2 mt-1 font-semibold leading-snug text-[#1f5c33]">
                {item.title}
              </div>
              <p className="text-sm leading-relaxed text-gray-600">
                {item.desc}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export const AboutStory = () => (
  <>
    <StoryHero />
    <OurJourney />
  </>
);

export default AboutStory;
